// Versão: 1.02
// Data..: 2026-09-25
// Modal reutilizável: overlay com fecho por backdrop, tecla ESC e botão ✕.
// Rodapé opcional (footer) para as ações do diálogo.
// v1.02: renderiza via portal no <body> para SEMPRE centralizar na viewport
// (evita deslocamento quando há ancestral com transform/filter/overflow).

import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function Modal({
  open,
  title,
  onClose,
  children,
  footer,
  maxWidth = 520,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  maxWidth?: number;
}) {
  // Fecha com ESC e trava o scroll do body enquanto aberto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  // IMPORTANTE: renderizar no <body> (portal) garante que o overlay `position: fixed`
  // se ancore na viewport e o modal fique centralizado. Sem o portal, um ancestral
  // com `transform` (ex.: animações .fade-in) faria o fixed se posicionar relativo
  // a esse ancestral, deslocando o modal. Ao criar/editar modais, mantenha este padrão.
  return createPortal(
    <div className="modal-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className="modal"
        style={{ maxWidth }}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          <button className="modal-x" onClick={onClose} aria-label="Fechar" title="Fechar">✕</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
